import { skipToken } from '@reduxjs/toolkit/dist/query'
import { useCallback, useMemo } from 'react'
import { Option } from '@typesApp/FormField'
import { AvailableChannelType, Insurer, ProviderType } from '@typesApp/Insurer'
import { useGetInsurersPublicQuery } from '@redux/queries/insurers'
import { getLang } from '@utils/getLang'

interface UsePublicInsurersReturn {
  insurers: Insurer[]
  options: Option[]
  isLoading?: boolean
  hasInStoreChannel?: boolean

  getInsurerProviderType: (insurerId: string | undefined) => ProviderType
  getInsurerWithCustomerForm: () => Insurer | undefined
}

const EMPTY_INSURERS: Insurer[] = []

export const usePublicInsurers = (skip?: boolean): UsePublicInsurersReturn => {
  const {
    data: insurers = EMPTY_INSURERS,
    isLoading,
    isFetching,
  } = useGetInsurersPublicQuery(skip ? skipToken : undefined)

  const options = useMemo(
    () => insurers.map(({ id, insurerName }) => ({ value: id, label: insurerName[getLang()] })),
    [insurers]
  )

  const hasInStoreChannel = useMemo(
    () => insurers.some(i => i.availableChannels?.includes(AvailableChannelType.IN_STORE)),
    [insurers]
  )

  const getInsurerProviderType = useCallback(
    (insurerId: string | undefined): ProviderType => {
      const insurer = insurers.find(i => i.id === insurerId)
      if (!insurer) return ProviderType.UNKNOWN

      return insurer?.availableChannels?.includes(AvailableChannelType.ONLINE)
        ? ProviderType.IN_NETWORK
        : ProviderType.OUT_OF_NETWORK
    },
    [insurers]
  )

  const getInsurerWithCustomerForm = useCallback((): Insurer | undefined => {
    return insurers.find(i => i.attachment && i.availableChannels?.includes(AvailableChannelType.CUSTOMER_FORM))
  }, [insurers])

  return {
    insurers,
    options,
    isLoading: isLoading || isFetching,
    hasInStoreChannel,
    getInsurerProviderType,
    getInsurerWithCustomerForm,
  }
}
